// src/lib/game/searchService.ts

import { supabase } from '../supabase';
import type { GameSearchFilters, Game, GameStats } from '../../types';

export interface SortOptions {
  sortBy?:
  | 'name'
  | 'year'
  | 'average_rating'
  | 'average_complexity'
  | 'play_time'
  | 'created_at'
  | 'relevance'
  | 'popularity';
  ascending?: boolean;
  searchRelevance?: boolean;
}

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    pageSize: number;
    totalPages: number;
    totalItems: number;
  };
}

// Remove acentos e normaliza para lowercase
const normalizeText = (t: string): string =>
  t.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

// Aplica filtros numéricos e de arrays a uma query builder
function applyFilters(
  q: ReturnType<typeof supabase.from>,
  f: GameSearchFilters
) {
  const {
    minPlayers,
    maxPlayers,
    minPlayTime,
    maxPlayTime,
    minComplexity,
    maxComplexity,
    year,
    categories = [],
    mechanics = [],
    themes = [],
    minAge,
  } = f;

  if (maxPlayers !== undefined) {
    q = q.eq('max_players', maxPlayers);
  }

  if (minPlayTime) q = q.gte('play_time', minPlayTime);
  if (maxPlayTime) q = q.lte('play_time', maxPlayTime);
  if (minComplexity) q = q.gte('average_complexity', minComplexity);
  if (maxComplexity) q = q.lte('average_complexity', maxComplexity);
  if (year) q = q.eq('year', year);
  if (minAge) q = q.gte('min_age', minAge);

  if (categories.length)
    q = q.contains('categories', categories.map((c) => c.toLowerCase()));
  if (mechanics.length)
    q = q.contains('mechanics', mechanics.map((m) => m.toLowerCase()));
  if (themes.length)
    q = q.contains('themes', themes.map((t) => t.toLowerCase()));

  return q;
}

// Definir um tipo para o resultado da RPC search_games
// Este tipo deve refletir EXATAMENTE o que a RPC retorna.
// Com base no erro do linter, parece que faltam vários campos do tipo Game completo.
interface GameRpcResult {
  id: string;
  name: string;
  year: number | null; // RPC pode retornar null para year
  description: string | null;
  image_url: string | null;
  // Estes são os campos que o linter indicou que a RPC retorna:
  categories: string[] | null;
  mechanics: string[] | null;
  themes: string[] | null;
  min_players: number | null;
  max_players: number | null;
  play_time: number | null;
  average_rating: number | null;
  average_complexity: number | null;
  // Adicionar outros campos que a RPC retorna e não estão em Game ou estão diferentes
  // rank: number; // Se rank for usado, adicione aqui
  total_count: number; // Este é específico da RPC para paginação
}

export async function getRankedGames(
  sortOptions: SortOptions = { sortBy: 'average_rating', ascending: false },
  filters: GameSearchFilters = {},
  page: number = 1,
  pageSize: number = 50,
  excludeGameIds: string[] = []
): Promise<PaginatedResponse<Game>> {
  const raw = filters.searchText?.trim() ?? '';
  const termNorm = normalizeText(raw);
  const isText = termNorm.length > 0;

  let all: Game[] = [];
  let totalItems = 0;

  // 1) Se há texto e searchRelevance, chama RPC
  if (isText && sortOptions.searchRelevance && excludeGameIds.length === 0) {
    const offset = (page - 1) * pageSize;
    const { data, error } = await supabase.rpc('search_games', {
      p_search_text: raw || null,
      p_categories: filters.categories?.length ? filters.categories : null,
      p_mechanics: filters.mechanics?.length ? filters.mechanics : null,
      p_themes: filters.themes?.length ? filters.themes : null,
      p_min_players: filters.minPlayers ?? null,
      p_max_players: filters.maxPlayers ?? null,
      p_min_play_time: filters.minPlayTime ?? null,
      p_max_play_time: filters.maxPlayTime ?? null,
      p_min_complexity: filters.minComplexity ?? null,
      p_max_complexity: filters.maxComplexity ?? null,
      p_year: filters.year ?? null,
      p_limit: pageSize,
      p_offset: offset,
    } as any);

    if (error) throw error;

    const rpcResults = (data as GameRpcResult[]) || [];
    totalItems = rpcResults.length > 0 ? rpcResults[0].total_count : 0;

    all = rpcResults.map(rpcGame => ({
      id: rpcGame.id,
      name: rpcGame.name,
      year: rpcGame.year,
      description: rpcGame.description,
      image_url: rpcGame.image_url,
      categories: rpcGame.categories,
      mechanics: rpcGame.mechanics,
      themes: rpcGame.themes,
      min_players: rpcGame.min_players,
      max_players: rpcGame.max_players,
      play_time: rpcGame.play_time,
      average_rating: rpcGame.average_rating,
      average_complexity: rpcGame.average_complexity,
      // Campos que precisam ser adicionados para satisfazer o tipo Game
      thumb_url: rpcGame.image_url, // Assumindo que thumb_url pode ser o mesmo que image_url ou precisa de lógica
      link: null, // Adicionar valor padrão ou null
      min_age: null, // Adicionar valor padrão ou null - este será o novo filtro
      complexity: rpcGame.average_complexity, // ou null se não puder ser derivado diretamente
      bgg_id: null,
      ludopedia_id: null,
      created_at: new Date().toISOString(), // Idealmente viria do banco de dados
      updated_at: new Date().toISOString(), // Idealmente viria do banco de dados
      artists: null,
      designers: null,
      store_products: [],
      stats: {
        average_rating: rpcGame.average_rating,
        average_complexity: rpcGame.average_complexity,
        // Outros campos de GameStats podem ser undefined ou 0
        owned_count: 0,
        wishlist_count: 0,
        rating_count: 0,
        play_count: 0,
        played_count: 0,
      },
      slug: null, // Idealmente viria do banco ou seria gerado
    } as Game));

  } else {
    // 2) Fallback: consulta normal via ILIKE + filtros
    // Usar count: 'planned' para evitar timeout em tabelas grandes. 
    // O count exato é muito custoso e 'planned' usa estatísticas do banco (muito mais rápido).
    let q = supabase.from('games').select('*', { count: 'planned' });
    if (excludeGameIds.length) {
      q = q.not('id', 'in', `(${excludeGameIds.join(',')})`);
    }
    q = applyFilters(q, filters);
    if (isText) {
      q = q.or([`name.ilike.%${raw}%`, `description.ilike.%${raw}%`].join(','));
    }
    if (!isText || sortOptions.sortBy !== 'relevance') {
      const sortBy =
        sortOptions.sortBy && sortOptions.sortBy !== 'relevance'
          ? sortOptions.sortBy
          : 'name';
      q = q.order(sortBy, { ascending: sortOptions.ascending ?? true, nullsFirst: false });
    }

    // OTIMIZAÇÃO: Se não estiver buscando por texto (que exige reordenação em memória),
    // aplicar paginação no lado do servidor para evitar timeout com muitos registros.
    if (!isText) {
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      q = q.range(from, to);
    }

    const { data, error, count } = await q;
    if (error) throw error;
    all = (data || []).map(dbGame => ({
      ...(dbGame as any),
      stats: dbGame.stats ? dbGame.stats as GameStats : undefined,
      // Certifique-se que todos os campos obrigatórios de Game estejam aqui ou sejam null/default
      // Exemplo: se dbGame não tem `slug`, e `slug` é `string | null` em `Game`
      slug: (dbGame as any).slug || null,
      created_at: (dbGame as any).created_at || new Date().toISOString(),
      updated_at: (dbGame as any).updated_at || new Date().toISOString(),
      // Adicionar outros campos faltantes com null ou valor padrão se necessário
    })) as Game[];
    totalItems = count ?? all.length;
  }

  // 3) Reordenação em memória: exato > prefixo > demais
  // Apenas se houver texto de busca e não tivermos paginado no servidor
  if (isText) {
    const exact = all.filter((g) => normalizeText(g.name) === termNorm);
    const prefix = all.filter((g) => {
      const n = normalizeText(g.name);
      return n.startsWith(termNorm) && n !== termNorm;
    });
    const rest = all.filter((g) => !exact.includes(g) && !prefix.includes(g));
    all = [...exact, ...prefix, ...rest];
  }

  // 4) Paginação
  const totalPages = Math.ceil(totalItems / pageSize);

  // Se já paginamos no servidor (!isText), 'all' já contém apenas a página atual.
  // Se não (!isText é false, ou seja, isText é true), 'all' tem tudo e precisamos fazer slice.
  let paged = all;
  if (isText) {
    const start = (page - 1) * pageSize;
    paged = all.slice(start, start + pageSize);
  }

  return {
    data: paged,
    pagination: { page, pageSize, totalPages, totalItems },
  };
}

export const searchGames = (
  filters: GameSearchFilters,
  page = 1,
  pageSize = 25
) => getRankedGames({ sortBy: 'name', ascending: true }, filters, page, pageSize);

export async function getPopularGames(limit = 10): Promise<Game[]> {
  const { data, error } = await supabase
    .from('games')
    .select('*')
    .not('average_rating', 'is', null)
    .order('average_rating', { ascending: false, nullsFirst: false })
    .limit(limit);
  if (error) throw error;
  return (data || []).map(dbGame => ({
    ...(dbGame as any),
    stats: dbGame.stats ? dbGame.stats as GameStats : undefined,
    slug: (dbGame as any).slug || null,
    created_at: (dbGame as any).created_at || new Date().toISOString(),
    updated_at: (dbGame as any).updated_at || new Date().toISOString(),
  })) as Game[];
}
